/**
 * Token storage for WorkOS auth tokens.
 *
 * Tokens are stored as HttpOnly cookies set by the backend.
 * The browser sends them automatically; the Cloudflare Worker
 * translates the access_token cookie into an Authorization: Bearer header.
 *
 * These helpers exist for backward compatibility during the migration.
 * The access token is no longer readable from JavaScript (HttpOnly),
 * so getAccessToken() returns a sentinel to indicate "cookies may be present".
 * Auth state is determined by calling /v1/auth/userinfo, not by checking tokens.
 */

/**
 * Returns true if the user has potentially valid auth cookies.
 * Since HttpOnly cookies are not readable from JS, we track auth state
 * via a lightweight flag in sessionStorage.
 */
export function hasAuthSession(): boolean {
  return sessionStorage.getItem('qc_auth_active') === 'true';
}

/**
 * Mark that an auth session is active (called after successful login/refresh).
 */
export function setAuthSessionActive(): void {
  sessionStorage.setItem('qc_auth_active', 'true');
}

/**
 * Clear the auth session flag (called on logout or auth failure).
 */
export function clearAuthSession(): void {
  sessionStorage.removeItem('qc_auth_active');
  // Also clean up any legacy localStorage tokens from the pre-cookie migration
  localStorage.removeItem('qc_access_token');
  localStorage.removeItem('qc_refresh_token');
}

// Legacy compatibility — these are used by api-client.ts refresh logic.
// With HttpOnly cookies, the refresh token is sent automatically in cookies.
// The body-based refresh_token is no longer needed for cookie-based clients.

/** @deprecated Access tokens are now HttpOnly cookies. Use hasAuthSession() instead. */
export function getAccessToken(): string | null {
  // Check legacy localStorage first (in case of migration mid-session)
  const legacy = localStorage.getItem('qc_access_token');
  if (legacy) return legacy;
  // If we have an active session, the cookie exists but isn't readable
  return hasAuthSession() ? '__httponly__' : null;
}

/** @deprecated Tokens are now set via HttpOnly cookies by the backend. */
export function setAccessToken(_token: string): void {
  setAuthSessionActive();
}

/** @deprecated Refresh tokens are now HttpOnly cookies. */
export function getRefreshToken(): string | null {
  return localStorage.getItem('qc_refresh_token');
}

/** @deprecated Tokens are now set via HttpOnly cookies by the backend. */
export function setRefreshToken(_token: string): void {
  // No-op — refresh token is in HttpOnly cookie
}

/** Clear all token state. */
export function clearTokens(): void {
  clearAuthSession();
}
